import React, { useEffect, useState } from "react";
import { TargetingIcon } from "orion-components/SharedComponents";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate } from "orion-components/i18n";
import EventBus from "orion-components/SharedComponents/EventBus";
import PropTypes from "prop-types";

const defaultCoords = ["0.0", "0.0"];

const CustomizedTextField = styled(TextField)({
	"& label": {
		color: "#9A9FA3",
		fontSize: 12
	},
	"& label.Mui-focused": {
		color: "#9A9FA3"
	},
	"& .MuiOutlinedInput-root": {
		"& fieldset": {
			borderColor: "#5E6571"
		},
		"&:hover fieldset": {
			borderColor: "#4DB5F4"
		},
		"&.Mui-focused fieldset": {
			borderColor: "#4DB5F4"
		}
	}
});

const propTypes = {
	disableTargetingIcon: PropTypes.bool,
	handleCoordinatesChange: PropTypes.func,
	customStyles: PropTypes.object,
	customWidth: PropTypes.number,
	showHeader: PropTypes.bool,
	showWithoutFeature: PropTypes.bool
};

const defaultProps = {
	handleCoordinatesChange: () => {},
	customStyles: {},
	customWidth: 163,
	disableTargetingIcon: false,
	showHeader: true,
	showWithoutFeature: false
};

const Coordinates = ({
	disableTargetingIcon,
	handleCoordinatesChange,
	customStyles,
	customWidth,
	showHeader,
	showWithoutFeature
}) => {
	const { feature } = useSelector(state => state.mapState?.mapTools) || { feature: null };
	const dir = useSelector(state => getDir(state));

	const [coordinates, setCoordinates] = useState([]);
	const [isReadOnly, setIsReadOnly] = useState(false);

	// Function to handle Circle or Polygon shapes
	const handleShape = (feature, type) => {
		let coords = [];
		if (type === "Circle" || feature.properties?.isCircle) {
			setIsReadOnly(true);
			coords.push(feature.geometry.coordinates[0][0]);
		} else if (type === "Polygon" || type === "Rectangle") {
			const ignoreLastCoords = feature.geometry.coordinates[0].slice(0, -1);
			if (feature.properties?.isRectangle || type === "Rectangle") {
				const filteredCoords = ignoreLastCoords.filter((element, index) => index % 2 === 0);
				coords = filteredCoords;
				setIsReadOnly(true);
			} else {
				coords = ignoreLastCoords;
			}
		}
		return coords;
	};

	// Function to convert coordinates to { lat, lng } objects
	const coordsToLatLng = (coords, type) => {
		const latLngArr = [];
		if (type === "Point") {
			latLngArr.push({ lat: coords[1], lng: coords[0] });
		} else {
			coords.forEach(data => {
				latLngArr.push({ lat: data[1], lng: data[0] });
			});
		}
		return latLngArr;
	};

	useEffect(() => {
		if (feature?.geometry?.coordinates) {
			const { type } = feature.geometry;
			let coords = handleShape(feature, type);
			if (!coords.length) {
				coords = feature.geometry.coordinates;
			}
			const latLngArr = coordsToLatLng(coords, type);
			setCoordinates(latLngArr);
		} else {
			setCoordinates([{ lat: 0, lng: 0 }]);
		}
	}, [feature]);

	if (feature !== null) {
		return (
			<div
				style={{
					...(dir === "ltr" && { marginLeft: 20 }),
					...(dir === "rtl" && { marginRight: 20 }),
					marginTop: 36,
					...customStyles
				}}
			>
				{showHeader && (
					<p
						style={{
							fontSize: 14,
							color: "#FFFFFF",
							letterSpacing: 0,
							opacity: "1",
							fontFamily: "roboto",
							marginBottom: 15
						}}
					>
						<Translate value="global.sharedComponents.shapeEditor.components.shared.coordinates.header" />
					</p>
				)}
				<div
					style={{
						display: "flex",
						fontFamily: "roboto",
						fontSize: 11,
						color: "#A7ACB0",
						letterSpacing: 0,
						opacity: "1"
					}}
				>
					<div
						style={{
							width: customWidth,
							...(dir === "ltr" && { marginLeft: 40 }),
							...(dir === "rtl" && { marginRight: 40 }),
							marginBottom: 6,
							...(disableTargetingIcon && { margin: 0 })
						}}
					>
						<p>
							<Translate value="global.sharedComponents.shapeEditor.components.shared.coordinates.latitude" />
						</p>
					</div>
					<div
						style={{
							width: customWidth,
							...(dir === "ltr" && { marginLeft: 20 }),
							...(dir === "rtl" && { marginRight: 20 }),
							marginBottom: 6
						}}
					>
						<p>
							<Translate value="global.sharedComponents.shapeEditor.components.shared.coordinates.longitude" />
						</p>
					</div>
				</div>
				{coordinates.map((item, index) => {
					return (
						<div key={index} style={{ display: "flex", marginBottom: 24 }}>
							{!disableTargetingIcon && (
								<TargetingIcon
									geometry={
										feature !== null
											? {
													coordinates: feature.properties?.isCircle
														? feature.geometry.coordinates
														: [item.lng, item.lat],
													type: feature.properties?.isCircle ? "Circle" : "Point"
												}
											: { coordinates: defaultCoords, type: feature?.geometry?.type }
									}
								/>
							)}
							<div
								style={{
									...(dir === "ltr" && { marginRight: 16 }),
									...(dir === "rtl" && { marginLeft: 16 })
								}}
							>
								<CustomizedTextField
									value={item.lat}
									variant="outlined"
									onChange={e => {
										const newLat = parseFloat(e.target.value);

										// Validate if the latitude is within the range [-90, 90]
										if (newLat >= -90 && newLat <= 90) {
											const newCoords = [...coordinates];
											newCoords[index].lat = newLat;
											setCoordinates(newCoords);
											handleCoordinatesChange([coordinates[index].lng, coordinates[index].lat]);
											EventBus.publish("updateFeatureCoordinates", {
												index,
												latOrLng: "lat",
												coordinate: newLat
											});
										}
									}}
									type="number"
									inputProps={{
										step: 1,
										style: {
											color: "#FFFFFF",
											fontSize: 12,
											fontFamily: "Roboto",
											textAlign: dir === "ltr" ? "left" : "right"
										}
									}}
									sx={{ width: customWidth, height: 42 }}
									InputProps={{
										readOnly: isReadOnly
									}} data-testid="orion-components-shared-components-shape-editor-components-shared-coordinates-customized-text-field"
								/>
							</div>
							<div
								style={{
									...(dir === "ltr" && { marginRight: 25 }),
									...(dir === "rtl" && { marginLeft: 25 })
								}}
							>
								<CustomizedTextField
									value={item.lng}
									variant="outlined"
									type="number"
									onChange={e => {
										const newLng = parseFloat(e.target.value);

										// Validate if the longitude is within the range [-180, 180]
										if (newLng >= -180 && newLng <= 180) {
											const newCoords = [...coordinates];
											newCoords[index].lng = newLng;
											setCoordinates(newCoords);
											handleCoordinatesChange([coordinates[index].lng, coordinates[index].lat]);
											EventBus.publish("updateFeatureCoordinates", {
												index,
												latOrLng: "lng",
												coordinate: newLng
											});
										}
									}}
									inputProps={{
										step: 1, // specify the step, if needed
										style: {
											color: "#FFFFFF",
											fontSize: 12,
											fontFamily: "Roboto",
											textAlign: dir === "ltr" ? "left" : "right"
										}
									}}
									sx={{ width: customWidth, height: 42 }}
									InputProps={{
										readOnly: isReadOnly
									}} data-testid="orion-components-shared-components-shape-editor-components-shared-coordinates-customized-text-field-2"
								/>
							</div>
						</div>
					);
				})}
			</div>
		);
	} else {
		return showWithoutFeature ? (
			<div
				style={{
					...(dir === "ltr" && { marginLeft: 20 }),
					...(dir === "rtl" && { marginRight: 20 }),
					marginTop: 36,
					...customStyles
				}}
			>
				<div
					style={{
						display: "flex",
						fontFamily: "roboto",
						fontSize: 11,
						color: "#A7ACB0",
						letterSpacing: 0,
						opacity: "1"
					}}
				>
					<div
						style={{
							width: customWidth,
							...(dir === "ltr" && { marginLeft: 40 }),
							...(dir === "rtl" && { marginRight: 40 }),
							marginBottom: 6,
							...(disableTargetingIcon && { margin: 0 })
						}}
					>
						<p>
							<Translate value="global.sharedComponents.shapeEditor.components.shared.coordinates.latitude" />
						</p>
					</div>
					<div
						style={{
							width: customWidth,
							...(dir === "ltr" && { marginLeft: 20 }),
							...(dir === "rtl" && { marginRight: 20 }),
							marginBottom: 6
						}}
					>
						<p>
							<Translate value="global.sharedComponents.shapeEditor.components.shared.coordinates.longitude" />
						</p>
					</div>
				</div>
				<div style={{ display: "flex", marginBottom: 24 }}>
					<div
						style={{
							...(dir === "ltr" && { marginRight: 16 }),
							...(dir === "rtl" && { marginLeft: 16 })
						}}
					>
						<CustomizedTextField
							value={coordinates[0]?.lat ?? 0}
							variant="outlined"
							onChange={e => {
								const newLat = parseFloat(e.target.value);

								// Validate if the latitude is within the range [-90, 90]
								if (newLat >= -90 && newLat <= 90) {
									const newCoords = [...coordinates];
									newCoords[0].lat = newLat;
									setCoordinates(newCoords);
									handleCoordinatesChange([coordinates[0].lng, coordinates[0].lat]);
									EventBus.publish("updateFeatureCoordinates", {
										index: 0,
										latOrLng: "lat",
										coordinate: newLat
									});
								}
							}}
							type="number"
							inputProps={{
								step: 1,
								style: {
									color: "#FFFFFF",
									fontSize: 12,
									fontFamily: "Roboto",
									textAlign: dir === "ltr" ? "left" : "right"
								}
							}}
							sx={{ width: customWidth, height: 42 }}
							InputProps={{
								readOnly: isReadOnly
							}}
							autoComplete="off" data-testid="orion-components-shared-components-shape-editor-components-shared-coordinates-customized-text-field-3"
						/>
					</div>
					<div
						style={{
							...(dir === "ltr" && { marginRight: 25 }),
							...(dir === "rtl" && { marginLeft: 25 })
						}}
					>
						<CustomizedTextField
							value={coordinates[0]?.lng ?? 0}
							variant="outlined"
							type="number"
							onChange={e => {
								const newLng = parseFloat(e.target.value);

								// Validate if the longitude is within the range [-180, 180]
								if (newLng >= -180 && newLng <= 180) {
									const newCoords = [...coordinates];
									newCoords[0].lng = newLng;
									setCoordinates(newCoords);
									handleCoordinatesChange([coordinates[0].lng, coordinates[0].lat]);
									EventBus.publish("updateFeatureCoordinates", {
										index: 0,
										latOrLng: "lng",
										coordinate: newLng
									});
								}
							}}
							inputProps={{
								step: 1, // specify the step, if needed
								style: {
									color: "#FFFFFF",
									fontSize: 12,
									fontFamily: "Roboto",
									textAlign: dir === "ltr" ? "left" : "right"
								}
							}}
							sx={{ width: customWidth, height: 42 }}
							InputProps={{
								readOnly: isReadOnly
							}}
							autoComplete="off" data-testid="orion-components-shared-components-shape-editor-components-shared-coordinates-customized-text-field-4"
						/>
					</div>
				</div>
			</div>
		) : null;
	}
};

Coordinates.propTypes = propTypes;
Coordinates.defaultProps = defaultProps;

export default Coordinates;
